import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

// Region is stored as the value Navan wants in X-ta-region, so US tenants hold
// an empty string and the proxy drops the header rather than sending "US".
const REGION_HOSTS: Record<string, string> = {
  "": "api.navan.com",
  EU: "app-fra.navan.com",
};

const navanHost = (region: string | undefined) =>
  REGION_HOSTS[region ?? ""] ?? REGION_HOSTS[""]!;

export const navan = defineConnector({
  identifier: "navan",
  name: "Navan",
  tagLine: "Corporate travel and expense",
  keywords: "navan tripactions business travel trips bookings expense",
  logo,
  protocol: "http",
  tags: ["travel"],
  defaultUrlPattern: ({ region }) => domainToRegex(navanHost(region)),
  auth: (app) =>
    oauthAuth.withHeaders([{ name: "X-ta-region", value: app.config.region }]),
  steps: [
    {
      title: "Choose your Navan region",
      items: [
        {
          kind: "select",
          label: "Region",
          defaultValue: "",
          target: { kind: "configuration", field: "region" },
          options: [
            { value: "", label: "United States" },
            { value: "EU", label: "Europe (Frankfurt)" },
          ],
          helperText:
            "EU tenants are served from a different host and must send a region header; sending the wrong one returns 500.",
        },
      ],
    },
    {
      title: "Create API credentials in Navan",
      items: [
        {
          kind: "markdown",
          content:
            "As an admin, go to **Admin › Travel › Settings › Integrations** and create a credential under **Navan API Credentials**. The secret key is shown once, when the credential is created. You can also restrict the credential to specific IP addresses there.",
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Secret key",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ region }) =>
      `https://${navanHost(region)}/ta-auth/oauth/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ region }) => `https://${navanHost(region)}/v1/users`,
  },
});
